import React, { useState } from 'react';
import SectionEyebrow from '../components/SectionEyebrow';
import { usePage, img } from '../lib/content';
import SmartMedia from '../components/SmartMedia';
import Seo from '../components/Seo';
import { api } from '../lib/api';
import { toast } from 'sonner';

const Venue = () => {
  const c = usePage('venue');
  const features = Array.isArray(c.features) ? c.features : [];
  const realWeddings = Array.isArray(c.realWeddings) ? c.realWeddings : [];
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', eventType: 'Wedding', date: '', guests: '', message: '' });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.date || !form.guests || !form.message) {
      toast.error('Please complete all fields so we can send your inquiry.');
      return;
    }
    try {
      await api.post('/api/leads', {
        type: 'venue',
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        message: form.message,
        meta: { eventType: form.eventType, date: form.date, guests: form.guests },
      });
      toast.success('Inquiry sent! Our events team will reach out within two business days.');
      setForm({ firstName: '', lastName: '', email: '', phone: '', eventType: 'Wedding', date: '', guests: '', message: '' });
    } catch {
      toast.error('Could not send your inquiry. Please try again.');
    }
  };

  return (
    <div className="bg-[#f7f1e3]">
      <Seo title="Wedding & Event Venue · Riverside Historic Mill · Millpond Gatherings, Northford CT" description="An intimate riverside wedding and event venue in a historic 1700s mill in Northford, CT — one celebration at a time. Weddings, rehearsal dinners & corporate events serving Wallingford, Hamden, Durham, Haddam, Chester & New Haven County." keywords="wedding venue Northford CT, riverside wedding venue Connecticut, historic mill wedding venue, intimate wedding venue New Haven County, micro wedding venue CT, rustic elegant wedding venue Connecticut, barn wedding venue CT, rehearsal dinner venue, bridal shower venue, engagement party venue, corporate event venue Connecticut, private dining events Northford, small wedding venue Wallingford Hamden Durham Haddam Chester" path="/venue" breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Private Events', path: '/venue' }]} />
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[640px] w-full overflow-hidden">
        <SmartMedia src={c.heroImage} alt="Wedding venue" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/25 to-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#f7f1e3] via-transparent to-transparent" style={{ backgroundSize: '100% 30%', backgroundRepeat: 'no-repeat', backgroundPosition: 'bottom' }} />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-32">
          <SectionEyebrow light>{c.heroEyebrow}</SectionEyebrow>
          <h1 className="font-serif text-[#f7f1e3] text-[64px] md:text-[96px] lg:text-[140px] tracking-[-0.02em] mt-4 leading-none">
            {c.heroTitle}
          </h1>
          <p className="text-[#f1ead9]/90 mt-8 max-w-xl text-base md:text-lg">
            {c.heroBody}
          </p>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-6">
            <SectionEyebrow>{c.overviewEyebrow}</SectionEyebrow>
            <h2 className="font-serif text-[#942222] text-[40px] md:text-[56px] lg:text-[64px] leading-[1.05] tracking-[-0.02em] mt-5">
              {c.overviewTitle}
            </h2>
          </div>
          <div className="lg:col-span-6 space-y-5 text-[#942222]/85 text-[16px] leading-relaxed">
            <p>{c.overviewBody1}</p>
            <blockquote className="font-serif italic text-[#b8923e] text-2xl lg:text-3xl my-8 leading-snug">
              “{c.overviewQuote}”
            </blockquote>
            <p>{c.overviewBody2}</p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 lg:py-24 bg-[#efe6d0]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {features.map((f) => (
            <div key={f.num || f.title} className="border-t border-[#942222]/30 pt-8">
              <p className="font-serif text-[#b8923e] text-2xl">{f.num}</p>
              <h3 className="font-serif text-[#942222] text-2xl lg:text-3xl mt-4 tracking-tight leading-tight">{f.title}</h3>
              <p className="mt-3 text-[#942222]/75 text-[14px] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REAL WEDDINGS */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionEyebrow>{c.realWeddingsEyebrow}</SectionEyebrow>
          <h2 className="font-serif text-[#942222] text-[44px] md:text-[60px] lg:text-[72px] leading-[1.05] tracking-[-0.02em] mt-5 max-w-3xl">
            {c.realWeddingsTitle}
          </h2>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-6">
            {realWeddings.map((w, i) => (
              <div key={i} className="group">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <SmartMedia src={w.image} alt={w.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
                <p className="mt-4 font-serif text-[#942222] text-xl md:text-2xl tracking-tight">{w.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRE */}
      <section className="py-24 lg:py-32 bg-[#942222] text-[#f1ead9]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <SectionEyebrow light>{c.inquireEyebrow}</SectionEyebrow>
          <h2 className="font-serif text-[44px] md:text-[60px] lg:text-[72px] leading-[1.05] tracking-[-0.02em] mt-5">
            {c.inquireTitle}
          </h2>
          <p className="mt-6 text-[#f1ead9]/80 max-w-xl text-[16px] leading-relaxed">
            {c.inquireBody}
          </p>

          <form onSubmit={onSubmit} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="First Name *" name="firstName" value={form.firstName} onChange={onChange} />
            <Field label="Last Name *" name="lastName" value={form.lastName} onChange={onChange} />
            <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} />
            <Field label="Phone *" name="phone" type="tel" value={form.phone} onChange={onChange} />
            <div>
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#f1ead9]/70">Event Type</label>
              <select name="eventType" value={form.eventType} onChange={onChange} className="mt-2 w-full bg-transparent border-b border-[#f1ead9]/40 py-3 text-[#f1ead9] outline-none focus:border-[#e2b95a] transition-colors">
                {['Wedding', 'Rehearsal Dinner', 'Corporate', 'Private Celebration', 'Other'].map((o) => (
                  <option key={o} value={o} className="bg-[#942222]">{o}</option>
                ))}
              </select>
            </div>
            <Field label="Preferred Date *" name="date" type="date" value={form.date} onChange={onChange} />
            <Field label="Guest Count *" name="guests" type="number" value={form.guests} onChange={onChange} />
            <div className="md:col-span-2">
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#f1ead9]/70">Tell us about your event *</label>
              <textarea name="message" rows={4} value={form.message} onChange={onChange} className="mt-2 w-full bg-transparent border-b border-[#f1ead9]/40 py-3 text-[#f1ead9] outline-none focus:border-[#e2b95a] transition-colors resize-none" />
            </div>
            <div className="md:col-span-2 mt-4">
              <button type="submit" className="px-10 py-4 bg-[#e2b95a] hover:bg-[#cea444] text-[#942222] text-[11px] tracking-[0.28em] uppercase font-semibold transition-colors">
                Submit Inquiry
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, type = 'text', value, onChange }) => (
  <div>
    <label className="text-[11px] tracking-[0.22em] uppercase text-[#f1ead9]/70">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className="mt-2 w-full bg-transparent border-b border-[#f1ead9]/40 py-3 text-[#f1ead9] outline-none focus:border-[#e2b95a] transition-colors"
    />
  </div>
);

export default Venue;
